/*
 * https://github.com/adoroszlai/leaflet-distance-markers
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2014- Doroszlai Attila, 2016- Phil Whitehurst
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy of
 * this software and associated documentation files (the "Software"), to deal in
 * the Software without restriction, including without limitation the rights to
 * use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of
 * the Software, and to permit persons to whom the Software is furnished to do so,
 * subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS
 * FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR
 * COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER
 * IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN
 * CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import L from 'leaflet'
import 'leaflet-geometryutil'

L.DistanceMarkers = L.LayerGroup.extend({
  initialize: function (line, map, options) {
    options = options || {}
    const offset = options.offset || 1000
    const showAll = Math.min(map.getMaxZoom(), options.showAll || 12)
    const cssClass = options.cssClass || 'dist-marker'
    const iconSize = options.iconSize !== undefined ? options.iconSize : [12, 12]

    const zoomLayers = {}
    // Get line coords as an array
    let coords = line
    if (typeof line.getLatLngs === 'function') {
      coords = line.getLatLngs()
    }
    // Get accumulated line lengths as well as overall length
    const accumulated = L.GeometryUtil.accumulatedLengths(line)
    const length = accumulated.length > 0 ? accumulated[accumulated.length - 1] : 0
    // Position in accumulated line length array
    let j = 0
    // Number of distance markers to be added
    const count = Math.floor(length / offset)

    for (let i = 1; i <= count; ++i) {
      const distance = offset * i
      // Find the first accumulated distance that is greater
      // than the distance of this marker
      while (j < accumulated.length - 1 && accumulated[j] < distance) {
        ++j
      }
      // Now grab the two nearest points either side of
      // distance marker position and create a simple line to
      // interpolate on
      const p1 = coords[j - 1]
      const p2 = coords[j]

      const m_line = L.polyline([p1, p2])
      const ratio = (distance - accumulated[j - 1]) / (accumulated[j] - accumulated[j - 1])
      const position = L.GeometryUtil.interpolateOnLine(map, m_line, ratio)
      const icon = L.divIcon({ className: cssClass, html: (distance / 1000), iconSize: iconSize })
      const marker = L.marker(position.latLng, { title: i, icon: icon })

      // visible only starting at a specific zoom level
      const zoom = this._minimumZoomLevelForItem(i, showAll)
      if (zoomLayers[zoom] === undefined) {
        zoomLayers[zoom] = L.layerGroup()
      }
      zoomLayers[zoom].addLayer(marker)
    }

    let currentZoomLevel = 0
    const markerLayer = this
    const updateMarkerVisibility = function () {
      const oldZoom = currentZoomLevel
      const newZoom = currentZoomLevel = map.getZoom()

      if (newZoom > oldZoom) {
        for (let i = oldZoom + 1; i <= newZoom; ++i) {
          if (zoomLayers[i] !== undefined) {
            markerLayer.addLayer(zoomLayers[i])
          }
        }
      } else if (newZoom < oldZoom) {
        for (let i = oldZoom; i > newZoom; --i) {
          if (zoomLayers[i] !== undefined) {
            markerLayer.removeLayer(zoomLayers[i])
          }
        }
      }
    }
    map.on('zoomend', updateMarkerVisibility)

    this._layers = {} // need to initialize before adding markers to this LayerGroup
    updateMarkerVisibility()
  },

  _minimumZoomLevelForItem: function (item, showAllLevel) {
    let zoom = showAllLevel
    let i = item
    while (i > 0 && i % 2 === 0) {
      --zoom
      i = Math.floor(i / 2)
    }
    return zoom
  }
})

L.Polyline.include({

  _originalOnAdd: L.Polyline.prototype.onAdd,
  _originalOnRemove: L.Polyline.prototype.onRemove,

  addDistanceMarkers: function () {
    if (this._map && this._distanceMarkers) {
      this._map.addLayer(this._distanceMarkers)
    }
  },

  removeDistanceMarkers: function () {
    if (this._map && this._distanceMarkers) {
      this._map.removeLayer(this._distanceMarkers)
    }
  },

  onAdd: function (map) {
    this._originalOnAdd(map)

    const opts = this.options.distanceMarkers || {}
    if (this._distanceMarkers === undefined && this.options.distanceMarkers) {
      this._distanceMarkers = new L.DistanceMarkers(this, map, opts)
    }
    if (opts.lazy === undefined || opts.lazy === false) {
      this.addDistanceMarkers()
    }
  },

  onRemove: function (map) {
    this.removeDistanceMarkers()
    this._originalOnRemove(map)
  }
})